import { useMemo, useState } from 'react';
import { Loader2, Rocket, Save, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';
import type { CreateEventBody } from '@/api/events.api';

/**
 * QuizForm — config del minijuego Quiz (familia quiz).
 *
 * Clona fielmente fede-quiz.png: Pregunta + 4 opciones con letra clickeable
 * (verde = correcta) + Tipo de premio + Premio en fichas + Tiempo para
 * responder + Duración del evento. Botones: Guardar Borrador / Crear y Lanzar /
 * Guardar como Template.
 *
 * Genera el objeto `config` (questions/prizeKind/amount/timeLimitSec) y llama
 * `eventsApi.create({ type:'quiz', name, config, ... })` vía useCreateEvent.
 */

const LETTERS = ['A', 'B', 'C', 'D'] as const;

type PrizeKind = 'fichas' | 'bono100' | 'bono200' | 'free_spin';

const PRIZE_OPTIONS: { value: PrizeKind; label: string }[] = [
  { value: 'fichas', label: 'Fichas' },
  { value: 'bono100', label: 'Bono 100%' },
  { value: 'bono200', label: 'Bono 200%' },
  { value: 'free_spin', label: 'Giro gratis' },
];

const PRIZE_AMOUNT_LABEL: Record<PrizeKind, string> = {
  fichas: 'Premio en fichas *',
  bono100: 'Monto del bono *',
  bono200: 'Monto del bono *',
  free_spin: 'Cantidad de giros *',
};

interface QuizFormProps {
  /** Callback opcional tras crear (cerrar dialog, etc.). */
  onCreated?: () => void;
}

export function QuizForm({ onCreated }: QuizFormProps) {
  const createMutation = useCreateEvent();

  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '', '', '']);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [prizeKind, setPrizeKind] = useState<PrizeKind>('fichas');
  const [amount, setAmount] = useState('2000');
  const [timeLimitSec, setTimeLimitSec] = useState('10');
  const [durationMin, setDurationMin] = useState('5');
  const [error, setError] = useState<string | null>(null);

  const isPending = createMutation.isPending;

  const setOption = (i: number, value: string) => {
    setOptions((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  // Validación: pregunta + al menos las 2 primeras opciones con texto.
  const validate = (): string | null => {
    if (!question.trim()) return 'La pregunta es obligatoria.';
    const filled = options.filter((o) => o.trim().length > 0);
    if (filled.length < 2) return 'Cargá al menos 2 opciones.';
    if (!options[correctIndex]?.trim())
      return 'La opción marcada como correcta no puede estar vacía.';
    if (prizeKind !== 'free_spin' && Number(amount) <= 0)
      return 'El premio debe ser mayor a 0.';
    return null;
  };

  const buildBody = (status: 'draft' | 'active'): CreateEventBody => {
    const cleanOptions = options.map((o) => o.trim());
    return {
      type: 'quiz',
      name: question.trim().slice(0, 120) || 'Quiz',
      status,
      durationMin: Number(durationMin) || 0,
      prizeKind,
      prizeAmount: prizeKind === 'free_spin' ? undefined : String(amount),
      config: {
        questions: [
          {
            q: question.trim(),
            options: cleanOptions,
            correctIndex,
          },
        ],
        prizeKind,
        amount: Number(amount) || 0,
        timeLimitSec: Number(timeLimitSec) || 10,
        requireAll: true,
        oneShot: true,
      },
    };
  };

  const submit = async (status: 'draft' | 'active', asTemplate = false) => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    const body = buildBody(status);
    if (asTemplate) {
      // Template = borrador reutilizable; marcamos el nombre.
      body.name = `[Template] ${body.name}`;
      body.status = 'draft';
    }
    await createMutation.mutateAsync(body);
    onCreated?.();
  };

  const amountLabel = useMemo(
    () => PRIZE_AMOUNT_LABEL[prizeKind],
    [prizeKind],
  );

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-amber-500">Crear Nuevo Quiz</h3>

      {/* Pregunta */}
      <div className="space-y-1.5">
        <Label>Pregunta *</Label>
        <textarea
          className="flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          placeholder="ej: ¿Cuál es la capital de Argentina?"
          value={question}
          onChange={(e) => setQuestion((e.target as HTMLTextAreaElement).value)}
        />
      </div>

      {/* Opciones */}
      <div className="space-y-2">
        <Label>Opciones (4 respuestas) *</Label>
        {options.map((opt, i) => {
          const selected = correctIndex === i;
          return (
            <div key={i} className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCorrectIndex(i)}
                title="Marcar como respuesta correcta"
                className={`flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-bold transition-colors ${
                  selected
                    ? 'bg-green-600 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {LETTERS[i]}
              </button>
              <Input
                placeholder={`Opción ${LETTERS[i]}`}
                value={opt}
                onChange={(e) =>
                  setOption(i, (e.target as HTMLInputElement).value)
                }
              />
            </div>
          );
        })}
        <p className="text-xs text-muted-foreground">
          Click en la letra para marcar la respuesta correcta (verde = correcta)
        </p>
      </div>

      {/* Premio / tiempo */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Tipo de premio *</Label>
          <Select
            value={prizeKind}
            onValueChange={(v) => setPrizeKind(v as PrizeKind)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIZE_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{amountLabel}</Label>
          <Input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount((e.target as HTMLInputElement).value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Tiempo para responder (seg) *</Label>
          <Input
            type="number"
            min="1"
            value={timeLimitSec}
            onChange={(e) =>
              setTimeLimitSec((e.target as HTMLInputElement).value)
            }
          />
        </div>
      </div>

      {/* Duración */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Duración del evento (min) *</Label>
          <Input
            type="number"
            min="1"
            value={durationMin}
            onChange={(e) =>
              setDurationMin((e.target as HTMLInputElement).value)
            }
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Acciones */}
      <div className="flex flex-wrap gap-3 pt-2">
        <Button
          variant="secondary"
          onClick={() => submit('draft')}
          disabled={isPending}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Save className="mr-2 h-4 w-4" />
          Guardar Borrador
        </Button>
        <Button onClick={() => submit('active')} disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Rocket className="mr-2 h-4 w-4" />
          Crear y Lanzar
        </Button>
        <Button
          variant="outline"
          onClick={() => submit('draft', true)}
          disabled={isPending}
        >
          <Box className="mr-2 h-4 w-4" />
          Guardar como Template
        </Button>
      </div>
    </div>
  );
}

export default QuizForm;
